import React, { useContext, useEffect, useState } from 'react'
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import MultiSelectEntity from '../RefineSearch/MultiSelectEntity'
import Popup from '../Common/Popup';

/**
 * The `FundSector` function is a React component that renders a filter for selecting fund sectors,
 * with a popup interface similar to RegionFilter.
 * @returns a JSX element.
 */
function FundSector({ fundSectorOptions, setFundSectorOptions, customPathname, onChange, label="Sector", isPopupOpen, setIsPopupOpen }) {

    const { FundSector, refineSearchFilters } = useContext(PeFilterContext);
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

    /* The `useEffect` hook is used to perform side effects in a React component. In this case, the
    effect is triggered whenever the dependencies (`FundSector`, `customPathname`,
    `refineSearchFilters`, `setFundSectorOptions`) change. */
    useEffect(() => {
        const industryIds = refineSearchFilters[customPathname]?.fundSector?.map(value => value.id) ?? [];
        setFundSectorOptions(FundSector?.map(industry => ({ ...industry, isChecked: industryIds.includes(parseInt(industry.id)) })))
    }, [FundSector, customPathname, refineSearchFilters, setFundSectorOptions])

    /* The `handleFundSectorChange` function is an event handler that is triggered when the user
    selects or deselects a fund sector option. It receives the event object `e` as a parameter. */
    const handleFundSectorChange = (e) => {
        const { name, checked, id, selectedEntities, filteredIds } = e.target;
        let currentFundSector = [];
    
        if (name === "allSelect") {
            currentFundSector = fundSectorOptions.map((fund) =>
                ({ ...fund, isChecked: checked })
            );
        } else if (name === "filteredSelect" && filteredIds) {
            currentFundSector = fundSectorOptions.map((fund) => {
                if (filteredIds.includes(fund.id)) {
                    return { ...fund, isChecked: checked };
                }
                return fund;
            });
        } else if (name === "selectedEntities" && selectedEntities) {
            const selectedEntityIds = selectedEntities.map(entity => parseInt(entity.id));
            currentFundSector = fundSectorOptions.map((fund) => ({
                ...fund,
                isChecked: selectedEntityIds.includes(fund.id) ? !fund.isChecked : fund.isChecked
            }));
        } else {
            currentFundSector = fundSectorOptions.map((fund) =>
                fund.id === parseInt(id) ? { ...fund, isChecked: checked } : fund
            );
        }
    
        setFundSectorOptions(currentFundSector);
        if (onChange) onChange(currentFundSector);
    };
    

    const selectedSectorCount = fundSectorOptions?.filter(option => option.isChecked)?.length ?? 0

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className={`rounded-lg px-4 py-1 text-gray-500 hover:bg-gray-100 text-sm shadow-md border border-gray-300 text-left flex items-center justify-between ${
                label === "Type" ? "w-full md:w-[90%]" : "w-full md:w-[90%]"
                }`}
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label} <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. {FundSector.slice(0,2).map(item => item.name).join(', ')}</span></span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {selectedSectorCount > 0 && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {selectedSectorCount}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)} label={label}>
                    <div className="p-4">
                        <MultiSelectEntity
                            handleEntityChange={handleFundSectorChange}
                            primaryEntity={fundSectorOptions}
                        />
                    </div>
                </Popup>
            )}
        </div>
    )
}

export default FundSector